import { useMemo } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { formatRelativeAge } from "../formatRelativeAge";

import { PanelTitleRow } from "./InfoPill";
import { isOfflineCode } from "../loadErrorText";
import { LoadErrorLine, StaleHint } from "./panelCards";
import { panelDays, panelRange } from "./progressWindow";
import { computeStalestExercises } from "./tierTwoMath";
import { makeTierTwoStyles } from "./tierTwoPanelStyles";
import type { ProgressDeps } from "./types";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { useSessionResultsFromDeps } from "./useSessionResults";

// Top 8 stalest exercises (max days-since-last among exercises ever logged).
export function ExerciseFrequencyPanel(deps: ProgressDeps) {
  const { theme } = deps;
  const { range } = deps;
  const windowDays = panelDays(range);
  const { t } = useTranslation();
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  const getName = useExerciseNameBySlug(catalog);
  const styles = useMemo(() => makeTierTwoStyles(theme), [theme]);

  const stalest = useMemo(() => {
    if (!results) return null;
    // Display name resolves here via FK→catalog; the aggregation carries
    // only the deleted-row snapshot.
    const win = panelRange(range);
    return computeStalestExercises(results, win.toMs, panelDays(win)).map((r) => ({
      ...r,
      name: getName(r.slug, r.snapshot),
    }));
  }, [results, getName, range]);

  const freqTitle = t("mobile.progress.frequency.title", {
    defaultValue: "Stalest exercises",
  });
  const freqHelp = t("mobile.progress.frequency.help", {
    days: windowDays,
    defaultValue:
      "Top 8 exercises sorted by days-since-last-logged, stalest first. The X/{{days}}d suffix counts the sets of the last {{days}} days. A long gap means it was either cut on purpose or the program drifted. Useful to spot accessory work that quietly disappeared.",
  });

  // #397: data kept from the last read comes before its failed refresh.
  if (error && stalest === null) {
    return (
      <View style={[styles.card, isOfflineCode(error) ? null : styles.cardError]}>
        <PanelTitleRow theme={theme} title={freqTitle} body={freqHelp} />
        <LoadErrorLine theme={theme} error={error} />
      </View>
    );
  }

  if (stalest === null) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={freqTitle} body={freqHelp} />
        <ActivityIndicator color={theme.palette.brand500} />
      </View>
    );
  }

  if (stalest.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={freqTitle} body={freqHelp} />
        {error ? <StaleHint theme={theme} /> : null}
        <Text style={styles.empty}>
          {t("mobile.progress.empty", {
            defaultValue: "No recent activity to compare",
          })}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <PanelTitleRow theme={theme} title={freqTitle} body={freqHelp} />
      {error ? <StaleHint theme={theme} /> : null}
      {stalest.map((row) => (
        <View key={row.slug} style={styles.freqRow}>
          <Text style={styles.freqLabel} numberOfLines={1}>
            {row.name}
          </Text>
          <Text style={styles.freqValue}>
            {/* Capped at weeks so stale rows stay distinguishable (PG-64). */}
            {formatRelativeAge(t, row.daysSince, { maxUnit: "weeks" })}
            {row.setsInWindow > 0 ? ` · ${row.setsInWindow}/${windowDays}d` : ""}
          </Text>
        </View>
      ))}
    </View>
  );
}
